import i18n from "../../../i18n";

let toastController = null;
let pendingMessages = [];
let toastSequence = 0;

const joinClassNames = (...values) =>
  values
    .flatMap((value) =>
      Array.isArray(value)
        ? value
        : typeof value === "string"
          ? value.split(" ")
          : []
    )
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const dispatchNotificationsRefresh = (options = {}) => {
  const {
    triggerNotificationsRefresh,
    notificationsRefreshRetries = 0,
    notificationsRefreshIntervalMs = 0,
    notificationsOptimisticActive = true,
  } = options || {};

  if (!triggerNotificationsRefresh || typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("notifications:refresh", {
      detail: {
        retries: notificationsRefreshRetries,
        intervalMs: notificationsRefreshIntervalMs,
        optimisticActive: notificationsOptimisticActive,
      },
    })
  );
};

const resolveDefaultSummary = (severity, providedSummary) => {
  if (providedSummary !== undefined && providedSummary !== null) {
    return providedSummary;
  }

  if (severity === "success") {
    return i18n.t("toastMessages.successSummary", {
      ns: "commonTranslation",
    });
  }

  if (severity === "error") {
    return i18n.t("toastMessages.errorSummary", {
      ns: "commonTranslation",
    });
  }

  return undefined;
};

const buildToastMessage = (severity, message, options = {}) => {
  const {
    autoClose,
    life,
    sticky,
    summary,
    closable = true,
    className,
    style,
    contentClassName,
    contentStyle,
  } = options || {};

  const shouldStick = sticky ?? autoClose === false;
  const resolvedLife =
    typeof life === "number"
      ? life
      : typeof autoClose === "number"
        ? autoClose
        : shouldStick
          ? undefined
          : 5000;

  return {
    id: `ls-prime-toast-${toastSequence += 1}`,
    severity,
    summary: resolveDefaultSummary(severity, summary),
    detail: message,
    sticky: shouldStick,
    life: resolvedLife,
    closable,
    className: joinClassNames("ls-prime-toast-message", className),
    ...(style ? { style } : {}),
    ...(contentClassName ? { contentClassName } : {}),
    ...(contentStyle ? { contentStyle } : {}),
  };
};

export const registerToastController = (controller) => {
  toastController = controller;

  if (pendingMessages.length > 0) {
    pendingMessages.forEach((message) => {
      toastController?.show?.(message);
    });
    pendingMessages = [];
  }
};

export const unregisterToastController = (controller) => {
  if (toastController === controller) {
    toastController = null;
  }
};

// Toasts that share a dedupeKey are shown once while the first one is still visible.
const visibleUntilByDedupeKey = new Map();

const isDuplicateVisibleToast = (dedupeKey, toastMessage) => {
  if (!dedupeKey) return false;

  const now = Date.now();
  const visibleUntil = visibleUntilByDedupeKey.get(dedupeKey) || 0;
  if (visibleUntil > now) return true;

  const visibleForMs = toastMessage.sticky ? Infinity : toastMessage.life || 0;
  visibleUntilByDedupeKey.set(dedupeKey, now + visibleForMs);
  return false;
};

export const showPrimeToast = (severity, message, options = {}) => {
  dispatchNotificationsRefresh(options);

  const toastMessage = buildToastMessage(severity, message, options);
  if (isDuplicateVisibleToast(options?.dedupeKey, toastMessage)) {
    return toastMessage;
  }
  if (toastController?.show) {
    toastController.show(toastMessage);
  } else {
    pendingMessages.push(toastMessage);
  }

  return toastMessage;
};

export const showSuccessToast = (message, options = {}) =>
  showPrimeToast("success", message, options);

export const showErrorToast = (message, options = {}) =>
  showPrimeToast("error", message, options);

export const showSuccess = showSuccessToast;
export const showError = showErrorToast;

export const dismissToast = (toastMessage) => {
  if (!toastMessage) return;

  if (toastController?.remove) {
    toastController.remove(toastMessage);
    return;
  }

  pendingMessages = pendingMessages.filter(
    (message) => String(message?.id || "") !== String(toastMessage?.id || "")
  );
};

export const clearToasts = () => {
  pendingMessages = [];
  toastController?.clear?.();
};
